import React from 'react';
import { Box, Typography, Tooltip, Chip, Card, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ProgressBar from './ProgressBar';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import { alpha } from '@mui/material/styles';
import { tokens } from '../theme';

// Import icon mapping utility
import { parseIconData } from '../Utils/iconMapping.js';

/**
 * A reusable SkillCard component
 * @param {object} props
 * @param {string} props.skillId - The skill ID
 * @param {string} props.skillCode - The skill code (e.g. H0101)
 * @param {string} props.name - The skill name
 * @param {string} props.description - The skill description
 * @param {string} props.priority - Priority level (Low, Medium, High)
 * @param {string} props.unitCode - The unit code this skill belongs to
 * @param {string} props.discipline - The discipline this skill belongs to
 * @param {string} props.disciplineColor - The color for discipline badge (from backend)
 * @param {string} props.disciplineIcon - The icon for discipline (from backend)
 * @param {object} props.skillLevels - Object containing skill level data
 * @param {object} props.skillLevelDescriptions - Object containing descriptions for each skill level
 * @param {string} props.internalRefValue - Value of internal reference
 * @param {string} props.externalRefValue - Value of external reference
 * @param {function} props.onView - Function to call when view icon is clicked
 * @param {function} props.onEdit - Function to call when edit icon is clicked
 * @param {function} props.onDelete - Function to call when delete icon is clicked
 */
const SkillCard = ({
  skillId,
  skillCode,
  name,
  description,
  priority = "Medium",
  unitCode,
  discipline = "",
  disciplineColor = "#00bcd4", // Default cyan color if not provided
  disciplineIcon,
  skillLevels = [],
  skillLevelDescriptions = {},
  internalRefValue = "",
  externalRefValue = "",
  onView,
  onEdit,
  onDelete
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return { bg: alpha(colors.redAccent[400], 0.2), text: colors.redAccent[400] };
      case "medium":
        return { bg: alpha(colors.Orange[400], 0.2), text: colors.Orange[400] };
      case "low":
        return { bg: alpha(colors.green[400], 0.2), text: colors.green[400] };
      default:
        return { bg: alpha(colors.grey[400], 0.2), text: colors.grey[400] };
    }
  };
  
  // Create a discipline style with backend-provided color
  const getDisciplineStyle = (color) => {
    return {
      bg: alpha(color || "#00bcd4", 0.1),  // Apply transparency to the color
      text: color || "#00bcd4"
    };
  };
  
  const priorityStyle = getPriorityColor(priority);
  const disciplineStyle = getDisciplineStyle(disciplineColor);
  
  return (
    <Card sx={{
      backgroundColor: "background.secondCard",
      border: 0,
      borderRadius: 2,
      borderLeft: `4px solid ${colors.primary[100]}`,
      boxShadow: 1,
      transition: 'all 0.3s',
      display: 'flex',
      padding:1,
      flexDirection: 'column',
      overflow: 'hidden',
      height: '100%', // Ensure all cards have same height in a row
      '&:hover': {
        boxShadow: 6,
        transform: 'translateY(-4px)'
      }
    }}>
      <Box sx={{ 
        p: { xs: 1.5, sm: 1.5, md: 2, lg: 2 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%', // Take full width of container
      }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          {/* Left side content */}
          <Box>
            {/* Discipline Badge with Icon */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              {disciplineIcon ? (
                (() => {
                  // Get the icon component from the utility
                  const IconComponent = parseIconData(disciplineIcon);
                  
                  return (
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(colors.primary[100], 0.2),
                        borderRadius: '8px',
                        mr: 0.75,
                        '& .MuiSvgIcon-root': { fontSize: '18px', color: '#fff' }
                      }}
                    >
                      {IconComponent && <IconComponent style={{ color: colors.primary[400], fontSize: '14px' }} />}
                    </Box>
                  );
                })()
              ) : null}
              <Chip 
                label={discipline}
                size="small"
                sx={{
                  bgcolor: disciplineStyle.bg,
                  color: disciplineStyle.text,
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  border: '0.01px solid',
                  borderColor: disciplineStyle.text,
                }}
              />
            </Box>
          </Box>

          {/* Right side - Priority Badge */}
          <Box>
            <Chip
              label={priority}
              size="small"
              sx={{
                bgcolor: priorityStyle.bg,
                color: priorityStyle.text,
                fontWeight: 500,
                fontSize: '0.85rem',
              }}
            />
          </Box>
        </Box>
        
        {/* Title */}
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.2rem', lg: '1.3rem' }, mb: 0.5 }}>
          {name}
        </Typography>
        
        {/* Skill Code and Unit */}
        <Box sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem', lg: '0.875rem' } }}>
              <Typography variant="body2" sx={{ color: colors.primary[400], fontWeight: 500 }}>
                {skillCode}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[600], mx: 0.5 }}>
                •
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[600] }}>
                {unitCode}
              </Typography>
            </Box>
          </Box>
          
          {/* Content Section */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', mt: 1 }}>
          {/* Description with line clamp */}
          <Box sx={{ mb: 2, pl: { xs: 1, sm: 2 } }}>
            <Typography variant="body2" sx={{
              color: colors.grey[700],
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minHeight: { xs: '3.6em', sm: '4.5em' },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            }}>
              {description || 'No description provided'}
            </Typography>
          </Box>
          
          {/* Skill Levels Section */}
          {skillLevels.length > 0 && (
            <Box sx={{ mb: 2, pl: { xs: 1, sm: 2 } }}>
              <Typography variant="body2" sx={{ 
                color: colors.grey[700], 
                fontWeight: 500, 
                mb: 0.75,
                fontSize: { xs: '0.8rem', sm: '0.875rem' } 
              }}>
                Skill levels:
              </Typography>
              
              {/* Skill Level Chips above Progress Bar */}
              <Box sx={{ mt: 1, mb: 2}}>
                {/* Skill Level Chips - positioned in 3 columns */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  mb: 1 
                }}>
                  <Box sx={{ 
                    width: { xs: '100%', sm: '30.33%' }, 
                    display: 'flex', 
                    justifyContent: 'center',
                    mb: { xs: 0.5, sm: 0 }
                  }}>
                    <Tooltip 
                      title={skillLevelDescriptions['Awareness'] || ''}
                      arrow
                      placement="top"
                    >
                      <Chip
                        label="Awareness"
                        size="small"
                        sx={{
                          bgcolor: alpha(colors.primary[300], 0.1),
                          color: colors.primary[400],
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          border: '0.1px solid',
                          borderColor: colors.primary[100],
                          height: { xs: '24px', sm: '26px' },
                        }}
                      />
                    </Tooltip>
                  </Box>
                  
                  <Box sx={{ 
                    width: { xs: '100%', sm: '33.33%' }, 
                    display: 'flex', 
                    justifyContent: 'center',
                    mb: { xs: 0.5, sm: 0 }
                  }}>
                    <Tooltip 
                      title={skillLevelDescriptions['Application'] || ''}
                      arrow
                      placement="top"
                    >
                      <Chip
                        label="Application"
                        size="small"
                        sx={{
                          bgcolor: alpha(colors.Orange[400], 0.1),
                          color: colors.Orange[400],
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          border: '0.1px solid',
                          borderColor: colors.Orange[400],
                          height: { xs: '24px', sm: '26px' },
                        }}
                      />
                    </Tooltip>
                  </Box>
                  
                  <Box sx={{ 
                    width: { xs: '100%', sm: '33.33%' }, 
                    display: 'flex', 
                    justifyContent: 'center',
                    mb: { xs: 0.5, sm: 0 }
                  }}>
                    <Tooltip 
                      title={skillLevelDescriptions['Management'] || ''}
                      arrow
                      placement="top"
                    >
                      <Chip
                        label="Management"
                        size="small"
                        sx={{
                          bgcolor: alpha(colors.redAccent[400], 0.1),
                          color: colors.redAccent[400],
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          border: '0.1px solid',
                          borderColor: colors.redAccent[400],
                          height: { xs: '24px', sm: '26px' },
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Box>
                
                {/* Multi-color progress bar */}
                <Box sx={{ display: 'flex', width: '90%', height: '6px', mx: 'auto' }}>
                  {/* Level 1: Awareness */}
                  <Box sx={{ 
                    width: '33.33%', 
                    height: '100%', 
                    bgcolor: alpha(colors.primary[300], 0.5), 
                    borderTopLeftRadius: '999px',
                    borderBottomLeftRadius: '999px'
                  }} />
                  
                  {/* Level 2: Application */}
                  <Box sx={{ 
                    width: '33.33%', 
                    height: '100%', 
                    bgcolor: alpha(colors.Orange[400], 0.7), 
                  }} />
                  
                  {/* Level 3: Management */}
                  <Box sx={{ 
                    width: '33.33%', 
                    height: '100%', 
                    bgcolor: alpha(colors.redAccent[500], 0.8), 
                    borderTopRightRadius: '999px',
                    borderBottomRightRadius: '999px'
                  }} />
                </Box>
              </Box>
            </Box>
          )}
          
          {/* Footer with References and Actions */}
          <Box >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              // gap: { xs: 0.5, sm: 2 },
              pl: { xs: 1, sm: 2 },
              width: { xs: '100%', sm: 'auto' }
            }}>
              {internalRefValue && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5, 
                  color: colors.grey[500], 
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}>
                  <LinkIcon sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }} />
                  <Typography variant="caption" sx={{ fontSize: 'inherit' }}>Internal.Ref: {internalRefValue}</Typography>
                </Box>
              )}
              
              {externalRefValue && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5, 
                  color: colors.grey[500], 
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}>
                  <LinkIcon sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }} />
                  <Typography variant="caption" sx={{ fontSize: 'inherit' }}>External.Ref: {externalRefValue}</Typography>
                </Box>
              )}
            </Box>
            
            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'flex-end',
              
              gap: 0.5, 
              alignContent: 'center',
              mr: { xs: 1, sm: 0 },
              mt: { xs: 1, sm: 1 } 
            }}>
              <Tooltip title="View skill details">
                <IconButton 
                  onClick={onView} 
                  size="small" 
                  sx={{
                    padding: { xs: '4px', sm: '6px' }
                  }}
                >
                  <VisibilityIcon sx={{ 
                    fontSize: { xs: '18px', sm: '20px' },
                    color: colors.grey[650],
                    '&:hover': { color: colors.Blue[500] } 
                  }} />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Edit skill">
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onEdit) onEdit();
                  }} 
                  size="small"
                  sx={{
                    padding: { xs: '4px', sm: '6px' }
                  }}
                >
                  <EditIcon sx={{ 
                    fontSize: { xs: '18px', sm: '20px' },
                    color: colors.grey[650],
                    '&:hover': { color: colors.primary[500] } 
                  }} />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Delete skill">
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onDelete) onDelete();
                  }} 
                  size="small"
                  sx={{
                    padding: { xs: '4px', sm: '6px' }
                  }}
                >
                  <DeleteIcon sx={{ 
                    fontSize: { xs: '18px', sm: '20px' },
                    color: colors.grey[650],
                    '&:hover': { color: colors.redAccent[500] } 
                  }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      
    </Card>
  );
};

export default SkillCard;
